
import React, { useRef } from 'react';
import Label from '../../../../ui/label';
import { useDispatch, useSelector } from 'react-redux';
import { FluentTagReset24Filled, IcRoundDownload } from '../../../../../assets/icon/iconify';
import { useFilesActions } from '../../../../../hooks/actions/use-files-actions';
import { resetCsvFile, selectCSVFile, selectExistingCsvFilePath, setCsvFile } from '../../../../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice';
import LoadingFile from '../../../../ui/loading-file';
import { useModalAccess } from '../../../../global/modal/use-modal-access';

interface Props {
    isRequired: boolean;
}

export default function ClientInputFileUpload({ isRequired }: Props) {
    const dispatch = useDispatch();
    const csvFile = useSelector(selectCSVFile);
    const { isLoadingDownloadMasterAttachedFile, handleDownloadMasterFile } = useFilesActions();
    const { isUpdateProposal } = useModalAccess();
    const existingCsvFilePath = useSelector(selectExistingCsvFilePath);
    const fileName = csvFile ? csvFile.name : isUpdateProposal ? existingCsvFilePath : '';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileNameFormatDisplay = isUpdateProposal ? 'Update file' : 'Choose file';

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            dispatch(setCsvFile(e.target.files[0]));
        }
    };

    const handleOpenFile = () => {
        fileInputRef.current?.click();
    };

    const handleClear = () => {
        dispatch(resetCsvFile());
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="w-full min-w-0 space-y-1.5">
            <Label
                label={`Attach Master List File ${isRequired ? '(required)' : '(optional)'}`}
                fontSize="text-xs"
                fontWeight="font-semibold"
                requiredText={isRequired ? ' *' : ''}
                isRequired={isRequired}
            />

            <div className="w-full min-w-0 rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                />

                <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    {/* FILE INFORMATION */}
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2] sm:h-11 sm:w-11">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <path d="M8 13h8" />
                                <path d="M8 17h5" />
                            </svg>
                        </div>

                        <div className="min-w-0 flex-1 pt-0.5">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <span className="text-xs font-semibold text-gray-800">
                                    Master List
                                </span>
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                    CSV
                                </span>
                            </div>

                            <p
                                className={`mt-1 break-words text-xs leading-5 ${fileName ? 'text-gray-600' : 'text-gray-400'}`}
                                title={fileName || undefined}
                            >
                                {fileName || 'No file selected'}
                            </p>

                            <p className="mt-1 text-[10px] text-gray-400 sm:text-xs">
                                CSV files only
                            </p>
                        </div>
                    </div>

                    {/* RESPONSIVE ACTIONS */}
                    <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:w-auto lg:shrink-0">
                        <button
                            type="button"
                            onClick={handleOpenFile}
                            className="inline-flex min-h-8 w-full items-center justify-center gap-2 rounded-md bg-[#186cb2] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[#145d99] focus:outline-none focus:ring-2 focus:ring-[#186cb2]/30 "
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{fileNameFormatDisplay}</span>
                        </button>

                        {isUpdateProposal ? (
                            <button
                                type="button"
                                onClick={handleDownloadMasterFile}
                                disabled={!existingCsvFilePath}
                                title={existingCsvFilePath ? 'Download existing master list' : 'No master list available'}
                                className="inline-flex min-h-8 w-full items-center justify-center gap-2 rounded-md border border-[#186cb2] bg-white px-3 py-2 text-xs font-medium text-[#186cb2] transition-colors hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 "
                            >
                                <IcRoundDownload width={15} height={15} />
                                <span>Download</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={!csvFile}
                                title={csvFile ? 'Remove selected file' : 'No file to clear'}
                                className="inline-flex min-h-8 w-full items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 "
                            >
                                <FluentTagReset24Filled width={15} height={15} color={csvFile ? '#ef4444' : '#9ca3af'} />
                                <span>Clear</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* HELPER INFORMATION */}
                <div className="mt-4 flex items-start gap-2 border-t border-gray-100 pt-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-gray-400">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>

                    <p className="text-[10px] leading-4 text-gray-500 sm:text-xs">
                        Please attach a valid CSV master list file.
                        {isRequired ? ' This file is required to continue.' : ' This file is optional.'}
                    </p>
                </div>
            </div>

            {isLoadingDownloadMasterAttachedFile && <LoadingFile />}
        </div>
    );
}
